import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/CaseStudies.css";
import { Link } from "react-router-dom";

// circular spinning
import facialrecognition from "../assets/casestudies/facialrecognition.png";
import jfs from "../assets/casestudies/jfs.png";
import taxgenie from "../assets/casestudies/taxgenie.png";
import deloitte from "../assets/casestudies/deloitte.png";

// backdrop pictures
import jfsbanner from "../assets/casestudies/jfsbanner.JPG";
// import caseAgencify from "../assets/casestudies/agencify.png";
import fer from "../assets/casestudies/fer.gif";
import taxgeniebg from "../assets/casestudies/taxgeniebg.png";
import deloittegif from "../assets/casestudies/deloitte.gif";
// import avabg from "../assets/casestudies/ava.jpg";
import aitechbanner from "../assets/misc/energy.gif";
import aitechhead from "../assets/casestudies/aitechhead.png";
import agentforgehead from "../case-studies/assets/agentforge/acn.png";
import agentforgebanner from "../case-studies/assets/agentforge/AGENTFORGE.gif";

const MotionLink = motion(Link);

/**
 * Case studies data:
 * [icon, background-image, link, year]
 */
const CASE_STUDIES = {
  "The Football Shirt Business I Built by Accident": [
    jfs,
    jfsbanner,
    "/case-studies/johns-football-shirts",
    2018,
  ],
  "A GOLDEN OPPORTUNITY: Facial Emotion Recognition": [
    facialrecognition,
    fer,
    "/case-studies/golden-opportunity",
    2022,
  ],
  "Deloitte: Tax Genie": [
    taxgenie,
    taxgeniebg,
    "/case-studies/deloitte-tax-genie",
    2023,
  ],
  "Deloitte: the $250,000 deal": [
    deloitte,
    deloittegif,
    "/case-studies/deloitte-250k-deal",
    2023,
  ],
  "THE COMPANY THAT FORCED ME TO OUTGROW MYSELF": [
    aitechhead,
    aitechbanner,
    "/case-studies/the-company-that-forced-me-to-outgrow-myself",
    2024,
  ],
  "WHEN AI AGENTS STOPPED BEING TOYS": [
    agentforgehead,
    agentforgebanner,
    "/case-studies/the-platform-that-taught-me-how-to-think-bigger",
    2025,
  ],
};

// 3D flip variants for the card
const flipVariants = {
  enter: (direction) => ({
    opacity: 0,
    rotateY: direction > 0 ? 60 : -60,
    rotateX: -4,
    z: -200,
  }),
  center: {
    opacity: 1,
    rotateY: 0,
    rotateX: -4,
    z: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 0.61, 0.36, 1],
    },
  },
  exit: (direction) => ({
    opacity: 0,
    rotateY: direction < 0 ? 60 : -60,
    rotateX: -4,
    z: -200,
    transition: {
      duration: 0.6,
      ease: [0.22, 0.61, 0.36, 1],
    },
  }),
};

const CaseStudies = ({ onViewCursorChange }) => {
  const entries = Object.entries(CASE_STUDIES);
  const total = entries.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [isTimelineView, setIsTimelineView] = useState(true);

  const goPrev = () => {
    if (activeIndex === 0) return;
    setDirection(-1);
    setActiveIndex((prev) => prev - 1);
  };

  const goNext = () => {
    if (activeIndex === total - 1) return;
    setDirection(1);
    setActiveIndex((prev) => prev + 1);
  };

  // Detect mobile via viewport width (for dots + some behaviour)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia("(max-width: 768px)");

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    setIsMobile(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener("change", handleChange);
    } else {
      mql.addListener(handleChange);
    }

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", handleChange);
      } else {
        mql.removeListener(handleChange);
      }
    };
  }, []);

  // current card data (icon is per-case)
  const [title, [icon, bg, link]] = entries[activeIndex];

  // Drag/swipe handler – used for both desktop & mobile
  const handleDragEnd = (event, info) => {
    const threshold = 50; // px swipe threshold

    if (info.offset.x < -threshold && activeIndex < total - 1) {
      // swipe left → next
      setDirection(1);
      setActiveIndex((prev) => prev + 1);
    } else if (info.offset.x > threshold && activeIndex > 0) {
      // swipe right → prev
      setDirection(-1);
      setActiveIndex((prev) => prev - 1);
    }
  };

  // Ensure VIEW cursor is off if component unmounts
  useEffect(() => {
    return () => {
      if (onViewCursorChange) onViewCursorChange(false);
    };
  }, [onViewCursorChange]);

  const handleSwitchView = () => {
    setIsTimelineView((prev) => !prev);
    if (onViewCursorChange) {
      onViewCursorChange(false);
    }
  };

  return (
    <section className="case-section" id="case-studies">
      <div className="case-sticky">
        <div className="case-inner">
          {/* Title */}
          <div className="case-header">
            <p className="case-title">CASE STUDIES</p>
          </div>

          {/* VIEW 1: Carousel / 3D card */}
          {!isTimelineView && (
            <>
              <div className="case-stack-wrapper">
                {/* Spinning badge that changes per case */}
                <div className="case-badge">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={title}
                      className="case-badge-rotator"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{
                        duration: 0.35,
                        delay: 0.4, // let the card flip first
                      }}
                    >
                      <motion.img
                        src={icon}
                        alt={`${title} badge`}
                        className="case-badge-img"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 18,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Single 3D-flipping card + arrows */}
                <div className="case-stack">
                  {/* Card */}
                  <AnimatePresence mode="wait" custom={direction}>
                    <MotionLink
                      key={title}
                      to={link}
                      className="case-card-link"
                      onMouseEnter={() => {
                        if (!isMobile && onViewCursorChange) {
                          onViewCursorChange(true);
                        }
                      }}
                      onMouseLeave={() => {
                        if (onViewCursorChange) {
                          onViewCursorChange(false);
                        }
                      }}
                    >
                      <motion.div
                        className="case-card"
                        style={{ backgroundImage: `url(${bg})` }}
                        custom={direction}
                        variants={flipVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.25}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="case-card-icon" />

                        <div className="case-card-meta">
                          <p className="case-card-label">Case study</p>
                          <h3 className="case-card-title">{title}</h3>
                          <span className="case-card-link-label">
                            View project ↗
                          </span>
                        </div>
                      </motion.div>
                    </MotionLink>
                  </AnimatePresence>

                  {/* Arrows – landscape / desktop only */}
                  {!isMobile && (
                    <>
                      <button
                        type="button"
                        className="case-arrow case-arrow--left"
                        onClick={goPrev}
                        disabled={activeIndex === 0}
                        aria-label="Previous case study"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="case-arrow case-arrow--right"
                        onClick={goNext}
                        disabled={activeIndex === total - 1}
                        aria-label="Next case study"
                      >
                        ›
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Carousel dots */}
              <div className="case-dots">
                {entries.map(([,], idx) => (
                  <button
                    key={idx}
                    className={
                      "case-dot" +
                      (idx === activeIndex ? " case-dot--active" : "")
                    }
                    onClick={() => {
                      if (idx === activeIndex) return;
                      setDirection(idx > activeIndex ? 1 : -1);
                      setActiveIndex(idx);
                    }}
                    aria-label={`Go to case ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}

          {/* VIEW 2: Flow-tree / timeline */}
          {isTimelineView && (
            <div className="case-timeline-wrapper">
              <div className="case-timeline">
                {entries.map(([entryTitle, [entryIcon, entryBg, entryLink, entryYear]], idx) => {
                  const isLeft = idx % 2 === 0;

                  return (
                    <motion.div
                      key={entryTitle}
                      className={
                        "case-timeline-item" +
                        (isLeft
                          ? " case-timeline-item--left"
                          : " case-timeline-item--right")
                      }
                      initial={{ opacity: 0, y: 30, scale: 0.98 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{
                        duration: 0.6,
                        ease: [0.22, 0.61, 0.36, 1],
                      }}
                    >
                      <div className="case-timeline-item-inner">
                        <div className="case-timeline-year">{entryYear}</div>

                        <MotionLink
                          to={entryLink}
                          className="case-timeline-card-link"
                          onMouseEnter={() => {
                            if (!isMobile && onViewCursorChange) {
                              onViewCursorChange(true);
                            }
                          }}
                          onMouseLeave={() => {
                            if (onViewCursorChange) {
                              onViewCursorChange(false);
                            }
                          }}
                        >
                          <motion.div
                            className="case-timeline-card"
                            style={{ backgroundImage: `url(${entryBg})` }}
                            whileHover={{ scale: 1.02 }}
                            transition={{
                              duration: 0.35,
                              ease: [0.22, 0.61, 0.36, 1],
                            }}
                          >
                            <div className="case-timeline-card-overlay" />
                            <div className="case-timeline-card-meta">
                              <p className="case-timeline-card-label">
                                Case study
                              </p>
                              <h3 className="case-timeline-card-title">
                                {entryTitle}
                              </h3>
                              <span className="case-timeline-card-link-label">
                                View project ↗
                              </span>
                            </div>
                          </motion.div>
                        </MotionLink>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* SWITCH VIEW BUTTON – always at the bottom of the section */}
          <div className="case-switch-wrapper">
            <button
              type="button"
              className="ai-main-link case-switch-button"
              onClick={handleSwitchView}
            >
              SWITCH VIEW
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
